import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    VStack,
    Text,
    Heading,
    Grid,
    GridItem,
    Flex,
    HStack,
    Badge,
    Textarea,
    Input,
    Image,
    SimpleGrid,
    Spinner,
    Alert,
    CloseButton,
    Button,
} from '@chakra-ui/react';
import { useColorModeValue } from '../../components/ui/color-mode';
import SelectBox from '../../components/shared/fields/SelectBox';
import ImageUploader from '../../components/shared/ImageUploader';
import ImagePreviewDialog from '../../components/shared/dialogs/ImagePreviewDialog';
import { uploadPath } from '../../utils/constants/insightsPath';
import { addRemovedImageUrl, clearFiles, clearImageChanges } from '../../store/filesSlice';

const ComplaintDetailsWithImages = ({
    isEditMode,
    errors,
    control,
    handleCancel,
    onSubmit,
    register,
    serviceRequestCategories,
    serviceRequestSubcategories,
    selectedComplaintType,
    handleSubmit,
}) => {
    const dispatch = useDispatch();
    const { images, imagesLoading, imagesError } = useSelector(state => state.complaint);
    const complaintData = useSelector((state) => state.complaint);
    const fileData = useSelector(state => state.files[uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD] || {
        files: [],
        existingImages: [],
        removedImageUrls: []
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
    const previousComplaintIdRef = useRef(null);

    useEffect(() => {
        const currentComplaintId = complaintData?.complaintId || complaintData?.id;
        
        // Only dispatch if the complaint ID has actually changed
        if (currentComplaintId && previousComplaintIdRef.current !== currentComplaintId) {
            previousComplaintIdRef.current = currentComplaintId;
            dispatch({
                type: "GET_COMPLAINT_IMAGES",
                payload: {
                    complaintId: currentComplaintId
                }
            });
        }
    }, [complaintData?.complaintId, complaintData?.id, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearFiles({ key: uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD }));
        };
    }, [dispatch]);

    const cardBg = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('white', 'gray.800')
    const complaintSectionBg = useColorModeValue('pink.50', 'pink.950');
    const productSectionBg = useColorModeValue('blue.50', 'blue.950');
    const productDetailsColors = useColorModeValue('gray.700', 'white');
    const imageSectionBg = useColorModeValue('purple.50', 'purple.950');
    const imageSectionTextColor = useColorModeValue('purple.800', 'purple.200');

    const handleImageClick = (image) => {
        setSelectedImage(image);
        setIsImageDialogOpen(true);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        handleSubmit((data) => {
            const imageChanges = {
                removedImageUrls: fileData.removedImageUrls,
            };
            onSubmit(data, imageChanges);
        })(e);
    };

    const handleCancelWithReset = () => {
        dispatch(clearImageChanges({ key: uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD }));
        handleCancel();
    };

    return (
        <>
            <Box p={6}>
                {/* Enhanced Details Panel with better card styling */}
                <Box
                    as="form"
                    onSubmit={handleFormSubmit}
                    p={8}
                    bg={cardBg}
                    borderRadius="xl"
                    boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
                    border="1px solid"
                    borderColor="gray.100"
                    position="relative"
                    overflow="hidden"
                    _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        bgGradient: 'linear(to-r, blue.400, purple.500, pink.400)',
                    }}
                >
                    {/* Header Section */}
                    <Box mb={8}>
                        <Flex justify="space-between" align="center" mb={2}>
                            <HStack spacing={3}>
                                <Box
                                    p={2}
                                    bg="blue.50"
                                    borderRadius="lg"
                                    border="1px solid"
                                    borderColor="blue.100"
                                >
                                    <Text fontSize="xl">📝</Text>
                                </Box>
                                <VStack align="start" spacing={0}>
                                    <Heading size="xl" fontWeight="700">
                                        Complaint Details
                                    </Heading>
                                    <Text fontSize="sm" color="gray.500">
                                        Review and manage complaint information
                                    </Text>
                                </VStack>
                            </HStack>
                            {isEditMode && (
                                <Badge
                                    colorScheme="orange"
                                    variant="solid"
                                    px={4}
                                    py={2}
                                    borderRadius="full"
                                    fontSize="xs"
                                    fontWeight="600"
                                    boxShadow="sm"
                                >
                                    ✏️ Edit Mode
                                </Badge>
                            )}
                        </Flex>
                    </Box>

                    <VStack spacing={8} align="stretch">
                        {/* Main Information Card */}
                        <Box
                            p={6}
                            borderRadius="xl"
                            bg={complaintSectionBg}
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <HStack mb={4} spacing={2}>
                                <Text fontSize="lg">📋</Text>
                                <Heading size="md" fontWeight="600">
                                    Basic Information
                                </Heading>
                            </HStack>

                            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                                <GridItem>
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm">🔖</Text>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="600"
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                            >
                                                Complaint ID
                                            </Text>
                                        </HStack>
                                        <Box
                                            p={3}
                                            bg={cardBg}
                                            borderRadius="lg"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            w="full"
                                        >
                                            <Text fontSize="sm" fontWeight="600" fontFamily="mono">
                                                {complaintData?.complaintId || complaintData?.id || '-'}
                                            </Text>
                                        </Box>
                                    </VStack>
                                </GridItem>

                                <GridItem>
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm">🎫</Text>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="600"
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                            >
                                                Reference Number
                                            </Text>
                                        </HStack>
                                        {isEditMode ? (
                                            <Input
                                                {...register('complaintReferenceNo')}
                                                placeholder="Enter reference number"
                                                defaultValue={complaintData?.complaintReferenceNumber}
                                                borderRadius="lg"
                                                border="2px solid"
                                                borderColor="gray.300"
                                                bg={cardBg}
                                                fontSize="sm"
                                                fontWeight="500"
                                                fontFamily="mono"
                                                _focus={{
                                                    borderColor: "blue.400",
                                                    boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)"
                                                }}
                                                _hover={{
                                                    borderColor: "gray.400"
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                p={3}
                                                bg={cardBg}
                                                borderRadius="lg"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                w="full"
                                            >
                                                <Text fontSize="sm" fontWeight="600" fontFamily="mono">
                                                    {complaintData?.complaintReferenceNumber || '-'}
                                                </Text>
                                            </Box>
                                        )}
                                    </VStack>
                                </GridItem>
                            </Grid>

                            <Grid templateColumns={{ base: '1fr', md: '1fr' }} gap={6} mt={6}>
                                <GridItem>
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm">🏷️</Text>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="600"
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                            >
                                                Title
                                            </Text>
                                        </HStack>
                                        {isEditMode ? (
                                            <Input
                                                {...register('title', { required: 'Title is required' })}
                                                placeholder="Enter complaint title"
                                                defaultValue={complaintData?.title}
                                                borderRadius="lg"
                                                border="2px solid"
                                                borderColor={errors.title ? "red.300" : "gray.300"}
                                                bg={cardBg}
                                                fontSize="md"
                                                fontWeight="500"
                                                _focus={{
                                                    borderColor: errors.title ? "red.400" : "blue.400",
                                                    boxShadow: `0 0 0 3px ${errors.title ? "rgba(245, 101, 101, 0.1)" : "rgba(66, 153, 225, 0.1)"}`
                                                }}
                                                _hover={{
                                                    borderColor: errors.title ? "red.400" : "gray.400"
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                p={3}
                                                bg={cardBg}
                                                borderRadius="lg"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                w="full"
                                            >
                                                <Text fontSize="md" fontWeight="600">
                                                    {complaintData?.title}
                                                </Text>
                                            </Box>
                                        )}
                                        {errors.title && (
                                            <HStack spacing={1}>
                                                <Text fontSize="xs">⚠️</Text>
                                                <Text color="red.500" fontSize="sm" fontWeight="500">
                                                    {errors.title.message}
                                                </Text>
                                            </HStack>
                                        )}
                                    </VStack>
                                </GridItem>
                            </Grid>

                            <Grid templateColumns={{ base: '1fr', md: '1fr' }} gap={6} mt={6}>
                                <GridItem>
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm">📄</Text>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="600"
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                            >
                                                Description
                                            </Text>
                                        </HStack>
                                        {isEditMode ? (
                                            <Textarea
                                                {...register('description', { required: 'Description is required' })}
                                                placeholder="Enter detailed description of the complaint..."
                                                minHeight="120px"
                                                defaultValue={complaintData?.description}
                                                borderRadius="lg"
                                                border="2px solid"
                                                borderColor={errors.description ? "red.300" : "gray.300"}
                                                bg={cardBg}
                                                fontSize="sm"
                                                _focus={{
                                                    borderColor: errors.description ? "red.400" : "blue.400",
                                                    boxShadow: `0 0 0 3px ${errors.description ? "rgba(245, 101, 101, 0.1)" : "rgba(66, 153, 225, 0.1)"}`
                                                }}
                                                _hover={{
                                                    borderColor: errors.description ? "red.400" : "gray.400"
                                                }}
                                                resize="vertical"
                                            />
                                        ) : (
                                            <Box
                                                p={3}
                                                bg={cardBg}
                                                borderRadius="lg"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                w="full"
                                                minHeight="120px"
                                            >
                                                <Text lineHeight="relaxed" fontSize="sm">
                                                    {complaintData?.description}
                                                </Text>
                                            </Box>
                                        )}
                                        {errors.description && (
                                            <HStack spacing={1}>
                                                <Text fontSize="xs">⚠️</Text>
                                                <Text color="red.500" fontSize="sm" fontWeight="500">
                                                    {errors.description.message}
                                                </Text>
                                            </HStack>
                                        )}
                                    </VStack>
                                </GridItem>
                            </Grid>
                        </Box>

                        {/* Product & Category Information */}
                        <Box
                            p={6}
                            bg={productSectionBg}
                            borderRadius="xl"
                            border="1px solid"
                            borderColor="blue.100"
                        >
                            <HStack mb={4} spacing={2}>
                                <Text fontSize="lg">🔧</Text>
                                <Heading size="md" color={productDetailsColors} fontWeight="600">
                                    Product & Category Details
                                </Heading>
                            </HStack>

                            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
                                <GridItem>
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm">🔢</Text>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="600"
                                                color={productDetailsColors}
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                            >
                                                Serial Number
                                            </Text>
                                        </HStack>
                                        <Box
                                            p={3}
                                            bg={cardBg}
                                            borderRadius="lg"
                                            border="1px solid"
                                            borderColor="blue.200"
                                            w="full"
                                        >
                                            <Text fontSize="md" fontWeight="600" color={productDetailsColors} fontFamily="mono">
                                                {complaintData?.serialNo}
                                            </Text>
                                        </Box>
                                    </VStack>
                                </GridItem>

                                <GridItem>
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm">📂</Text>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="600"
                                                color={productDetailsColors}
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                            >
                                                Category
                                            </Text>
                                        </HStack>
                                        {isEditMode ? (
                                            <Box w="full">
                                                <SelectBox
                                                    label=""
                                                    invalid={!!errors.complaintType}
                                                    errorText={errors.complaintType?.message}
                                                    control={control}
                                                    name="complaintType"
                                                    items={serviceRequestCategories}
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                p={3}
                                                bg={cardBg}
                                                borderRadius="lg"
                                                border="1px solid"
                                                borderColor="blue.200"
                                                w="full"
                                            >
                                                <Text fontSize="md" fontWeight="600" color={productDetailsColors}>
                                                    {complaintData?.categoryObject?.label}
                                                </Text>
                                            </Box>
                                        )}
                                    </VStack>
                                </GridItem>

                                <GridItem>
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm">📁</Text>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="600"
                                                color={productDetailsColors}
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                            >
                                                Sub Category
                                            </Text>
                                        </HStack>
                                        {isEditMode ? (
                                            <Box w="full">
                                                <SelectBox
                                                    label=""
                                                    invalid={!!errors.complaintSubType}
                                                    errorText={errors.complaintSubType?.message}
                                                    control={control}
                                                    name="complaintSubType"
                                                    disabled={!isEditMode || !selectedComplaintType || selectedComplaintType.length === 0}
                                                    items={
                                                        selectedComplaintType && selectedComplaintType.length > 0
                                                            ? serviceRequestSubcategories[selectedComplaintType[0]] || []
                                                            : []
                                                    }
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                p={3}
                                                bg={cardBg}
                                                borderRadius="lg"
                                                border="1px solid"
                                                borderColor="blue.200"
                                                w="full"
                                            >
                                                <Text fontSize="md" fontWeight="600" color={productDetailsColors}>
                                                    {complaintData?.complaintSubCategoryObject?.label || complaintData?.complaintSubCategory}
                                                </Text>
                                            </Box>
                                        )}
                                    </VStack>
                                </GridItem>
                            </Grid>
                        </Box>

                        {/* Images Section */}
                        <Box
                            p={6}
                            bg={imageSectionBg}
                            borderRadius="xl"
                            border="1px solid"
                            borderColor="purple.100"
                        >
                            <HStack mb={6} spacing={2}>
                                <Text fontSize="lg">📷</Text>
                                <VStack align="start" spacing={0}>
                                    <Heading size="md" color={imageSectionTextColor} fontWeight="600">
                                        Complaint Images
                                    </Heading>
                                    <Text fontSize="sm" color="purple.500">
                                        Visual evidence and documentation
                                    </Text>
                                </VStack>
                            </HStack>

                            {imagesLoading ? (
                                <Flex justify="center" align="center" py={12} direction="column" spacing={4}>
                                    <Spinner size="xl" color="purple.500" thickness="4px" />
                                    <Text mt={4} color={imageSectionTextColor} fontWeight="500">
                                        Loading images...
                                    </Text>
                                </Flex>
                            ) : imagesError ? (
                                <Alert status="error" borderRadius="lg" bg="red.50" border="1px solid" borderColor="red.200">
                                    <Alert />
                                    <Text color="red.700">{imagesError}</Text>
                                </Alert>
                            ) : (
                                <VStack align="start" spacing={6}>
                                    {images && images.length > 0 && (
                                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4} w="full">
                                            {images
                                                .filter(image => !fileData?.removedImageUrls?.includes(image.url))
                                                .map((image, index) => (
                                                    <Box
                                                        key={index}
                                                        position="relative"
                                                        borderRadius="xl"
                                                        overflow="hidden"
                                                        border="2px solid"
                                                        borderColor="purple.200"
                                                        bg={cardBg}
                                                        _hover={{
                                                            borderColor: "purple.400",
                                                            transform: "translateY(-4px)",
                                                            boxShadow: "0 8px 25px rgba(128, 90, 213, 0.15)"
                                                        }}
                                                        transition="all 0.3s ease"
                                                        cursor="pointer"
                                                        onClick={() => handleImageClick(image)}
                                                    >
                                                        <Image
                                                            src={image.url}
                                                            alt={image.name || `Complaint image ${index + 1}`}
                                                            width="100%"
                                                            height="120px"
                                                            objectFit="cover"
                                                        />
                                                        <Box
                                                            position="absolute"
                                                            bottom={0}
                                                            left={0}
                                                            right={0}
                                                            bg="linear-gradient(transparent, rgba(0,0,0,0.7))"
                                                            p={2}
                                                        >
                                                            <Text color={textColor} fontSize="xs" fontWeight="500" isTruncated>
                                                                {image.name || `Image ${index + 1}`}
                                                            </Text>
                                                        </Box>
                                                        {isEditMode && (
                                                            <CloseButton
                                                                size="sm"
                                                                position="absolute"
                                                                top="8px"
                                                                right="8px"
                                                                bg="red.500"
                                                                borderRadius="full"
                                                                boxShadow="md"
                                                                _hover={{
                                                                    bg: "red.600",
                                                                    transform: "scale(1.1)"
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    dispatch(addRemovedImageUrl({
                                                                        key: uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD,
                                                                        imageUrl: image.url
                                                                    }));
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                ))}
                                        </SimpleGrid>
                                    )}

                                    {isEditMode && images.length < 3 && (
                                        <Box w="full">
                                            <ImageUploader
                                                isEdit={complaintData.isEdit}
                                                path={uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD}
                                                existingImages={images || []}
                                                showExistingImages={true}
                                                maxImages={3 - images.length}
                                                onRemoveExisting={(removedImage, index) => {
                                                    // console.log('Removed existing image:', removedImage, 'at index:', index);
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {(!images || (images.length === 0)) && (
                                        <Box
                                            w="full"
                                            p={8}
                                            textAlign="center"
                                            border="2px dashed"
                                            borderColor="purple.200"
                                            borderRadius="xl"
                                            bg={cardBg}
                                        >
                                            <Text fontSize="4xl" mb={2}>📷</Text>
                                            <Text color="purple.600" fontWeight="500">
                                                No images uploaded yet
                                            </Text>
                                            <Text color="purple.500" fontSize="sm">
                                                Images help provide better context for the complaint
                                            </Text>
                                        </Box>
                                    )}
                                </VStack>
                            )}
                        </Box>
                    </VStack>

                    {/* Action Buttons */}
                    {isEditMode && (
                        <Box mt={8} pt={6} borderTop="2px solid" borderColor="gray.100">
                            <Flex justify="flex-end">
                                <HStack spacing={4}>
                                    <Button
                                        onClick={handleCancelWithReset}
                                        variant="outline"
                                        colorScheme="gray"
                                        size="lg"
                                        borderRadius="xl"
                                        fontWeight="600"
                                        px={8}
                                        borderWidth="2px"
                                        _hover={{
                                            bg: "gray.50",
                                            transform: "translateY(-1px)",
                                            boxShadow: "md"
                                        }}
                                        transition="all 0.2s"
                                    >
                                        ❌ Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        colorScheme="blue"
                                        size="lg"
                                        borderRadius="xl"
                                        fontWeight="600"
                                        px={8}
                                        bgGradient="linear(to-r, blue.400, blue.600)"
                                        _hover={{
                                            bgGradient: "linear(to-r, blue.500, blue.700)",
                                            transform: "translateY(-1px)",
                                            boxShadow: "lg"
                                        }}
                                        transition="all 0.2s"
                                    >
                                        💾 Save Changes
                                    </Button>
                                </HStack>
                            </Flex>
                        </Box>
                    )}
                </Box>
            </Box>
            {/* Image Preview Dialog */}
            <ImagePreviewDialog
                isOpen={isImageDialogOpen}
                onOpenChange={(details) => {
                    setIsImageDialogOpen(details.open);
                    if (!details.open) {
                        setSelectedImage(null);
                    }
                }}
                selectedImage={selectedImage}
                title={selectedImage?.name}
                defaultTitle="Complaint Image"
                width="80vw"
                height="80vh"
            />
        </>
    );
};

export default ComplaintDetailsWithImages;